import {
  BlurFilter,
  Container,
  ITextStyle,
  Sprite,
  TextMetrics,
  TextStyle,
  TextStyleFontWeight,
  Texture,
  TilingSprite,
  Text,
} from 'pixi.js';
import { TextLine } from '../../../shape/shapes/text/text-line';
import { TextWord } from '../../../shape/shapes/text/text-word';
import { TextLetter } from '../../../shape/shapes/text/text-letter';
import gsap from 'gsap';
import {
  TextAnimationStep,
  ZoomAnimation,
} from '../../../animation/text-animation.service';
import { Subscription } from 'rxjs';
import {
  CellPosition,
  selectSelectedShape,
  selectShape,
  StudioAnimation,
  StudioCell,
  StudioTextAnimationStep,
} from '..';
import { StudioSceneShape } from '../../../shape/shapes/general/root/studio-scene-shape';
import { RectangleController } from '../../../../elements/util/rectangle-controller/rectangle-controller';
import { StudioShape, StudioShapeConfig } from '../shapes/studio-shape';
import { TextBreak } from '../layout/studio-layout.service';
import {
  GradientDirection,
  RectangleElement,
} from '../../../shape/shapes/primitive/rectangle-element';
import { CanvasService } from '../../../../services/canvas/canvas.service';

export type StudioTextAnimationType =
  | 'zoom-fade'
  | 'fade-by-letters'
  | 'translate-fade';

export interface StudioShapeDescriptor {
  cell: StudioCell;
  metaPosition?: {
    vertical: 'top' | 'center' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
    shiftX?: number;
    shiftY?: number;
  };
  background?: StudioShapeBackground;
  animationFunctions?: Record<string, StudioAnimation>;
  startPosition?: CellPosition;
}

export interface StudioShapeBackground {
  type: 'color' | 'gradient';
  padding?: number;
  radius?: number;
  color?: string;
  opacity?: number;
  gradient?: {
    type: 'linear' | 'radial';
    direction?: GradientDirection;
    colors?: string[];
  };
  stroke?: {
    color: string;
    width: number;
  };
}

export interface StudioTextShapeDescriptor extends StudioShapeDescriptor {
  x?: number;
  y?: number;
  text?: string;
  textBreak?: TextBreak;
  lines?: Array<string[]>;
  dims?: [number, number];
  capitalization?: 'normal' | 'capital';
  align?: 'left' | 'center' | 'right';
  vAlign?: 'top' | 'center' | 'bottom';
  rowGap?: number;
  fontFamily?: string;
  fontWeight?: number;
  fontStyle?: 'normal' | 'italic' | 'oblique';

  lineHeight?: number;
  fill?: string;
  outline?: string;
  outlineWidth?: number;
  blur?: {
    amount: number;
    quality: number;
  };
}

export class StudioTextShape extends StudioShape<StudioTextShapeDescriptor> {
  lines: TextLine[] = [];
  selectionStartIndex: number = null;
  selectionEndIndex: number = null;
  currentSelectionIndex: number = null;

  selectedEditMode: boolean;
  selected?: boolean;
  // Core properties used by TextLine/TextWord/TextLetter
  baseLineHeight: number = 0;
  maxLineWidth: number = 0;
  fontSize: number = 28;
  spaceWidth: number = 0;
  netLineHeight: number = 0;
  elementLevel: 'line' | 'word' | 'letter' = 'letter';
  characterWidthMap: Record<string, number> = {};
  loadPromise: Promise<void> = Promise.resolve();
  maskRectangle: RectangleElement = null;
  private letterIndex = 0;
  private cellSubscription: Subscription | null = null;
  get store() {
    return this.shape?.store;
  }
  get lineHeight() {
    return this.descriptor.lineHeight || 1;
  }

  get paddingValue() {
    return this.descriptor.cell.padding == undefined
      ? 0.01
      : (this.descriptor.cell.padding as number);
  }

  get words(): TextWord[] {
    return this.lines.reduce((arr, line) => [...arr, ...line.words], []);
  }

  get groupShapeParent() {
    return null;
  }

  get letters(): TextLetter[] {
    return this.words.reduce((arr, word) => [...arr, ...word.letters], []);
  }

  get grossLineHeight(): number {
    return this.netLineHeight * 1.25;
  }

  get lineHeightOffset(): number {
    return (this.grossLineHeight - this.netLineHeight) / 2;
  }

  get cs() {
    return this.shape?.cs;
  }

  get service() {
    return this.shape?.studioService?.shapeService;
  }

  get textConfig() {
    return { opacity: 1 };
  }

  get fontFamily() {
    return this.descriptor.fontFamily || this.studioService.currentFontFamily;
  }

  get elementStyle(): Partial<ITextStyle> {
    return {
      fontSize: this.fontSize,
      fontFamily: this.fontFamily,
      fontWeight: String(
        this.descriptor.fontWeight || 500,
      ) as TextStyleFontWeight,
      fontStyle: this.descriptor.fontStyle || 'normal',
      fill: this.descriptor.fill || '#000000ff',
      wordWrap: false,
      align: this.descriptor.cell.align || this.descriptor.align || 'center',
    };
  }

  getType() {
    return 'studio-text-shape';
  }

  get x() {
    return this.descriptor.x;
  }
  get y() {
    return this.descriptor.y;
  }
  constructor(
    public readonly shape: StudioSceneShape,
    readonly id: string,
    descriptor: StudioTextShapeDescriptor,
    config?: StudioShapeConfig,
  ) {
    super(shape, id, descriptor, config);
    // -- // -- //

    this.subscribeToCellDescriptor();
    this.loadPromise = this.init().then(() => {
      this.alignInCell(this.descriptor.cell);
      if (!this.config?.preview) {
        this.initRC();
      }

      // setTimeout(() => {
      //   if (this.shape.positionIndex == 0) {
      //     console.log('colorizeText');
      //     this._colorizeText();
      //   }
      // }, 2000);
      // this._colorizeText();
    });
    // -- // -- //

    this.store.select(selectSelectedShape).subscribe(res => {
      const { id } = res || {};
      if (id == this.id) {
        this.selected = true;
        // this.rc.show();
      } else {
        this.selected = false;
        this.rc?.hide();
        // console.log('hide');
      }
    });
  }
  initContainers() {}
  async update(descriptor: StudioTextShapeDescriptor) {
    // console.warn('update', descriptor);
    this.descriptor = descriptor;
    this.container.removeChildren();
    await this.init();
    this.alignInCell(this.descriptor.cell);
    this.rc?.patch({
      width: this.maxLineWidth,
      height: this.textHeight,
    });
    if (this.shape.positionIndex == 0) {
      console.log('update > colorizeText');
    }
  }

  alignOffsetX = 0;
  alignOffsetY = 0;

  alignInCell(cell: StudioCell, noOverride?: boolean) {
    if (!cell) {
      return;
    }

    // if (!noOverride) { // -- //
    // this.descriptor = { ...this.descriptor, cell };
    // }

    const { x, y, w, h } = this.cellDimsnByCell(cell);
    let { align, vAlign } = cell;

    align ||= 'center';
    vAlign ||= 'center';

    const actualWidth = this.maxLineWidth;
    const actualHeight = this.baseLineHeight * this.lines.length;

    this.container.scale.set(1, 1);

    let containerX = 0,
      containerY = 0;

    switch (align) {
      case 'left':
        containerX = 0;
        break;
      case 'right':
        containerX = w - actualWidth;
        break;
      case 'center':
      default:
        containerX = (w - actualWidth) / 2;
        break;
    }

    switch (vAlign) {
      case 'top':
        containerY = 0;
        break;
      case 'bottom':
        containerY = h - actualHeight;
        break;
      case 'center':
      default:
        containerY = (h - actualHeight) / 2;
        break;
    }

    // this.container.position.set(containerX, containerY);
    // this.lines.map(line => {
    //   line.container.x = containerX;
    //   line.container.y = containerY + line.index * this.absoluteLineHeight;
    // });

    this.alignOffsetX = containerX;
    this.alignOffsetY = containerY;

    this.setPosition(x + containerX, y + containerY);
  }

  get absoluteLineHeight() {
    return this.lineHeight * this.fontSize * 1.2;
  }

  draggedDims: [number, number];
  rc: RectangleController;

  unMovedCellDims: {
    x: number;
    y: number;
    w: number;
    h: number;
  };
  pseudoAnimation(animation: StudioAnimation): gsap.core.Timeline {
    const timeline = super.pseudoAnimation(animation);
    // const { letter, word } = animation as StudioTextAnimationStep;
    // if (letter) {
    //   this.elementLevel = 'letter';
    // }
    // if (word) {
    //   this.elementLevel = 'word';
    // }
    // this.refresh();
    return timeline;
  }
  get textHeight() {
    const firstLineHeight = this.fontSize * 1.2;
    let textHeight = firstLineHeight;
    for (let i = 1; i < this.lines.length; i++) {
      textHeight += this.absoluteLineHeight;
    }
    return textHeight;
  }

  unMovedLineHeight: number;

  initRC() {
    if (!this || !this.shape.text) {
      return;
    }

    this.rc = new RectangleController(
      this,
      {
        id: this.id,
        width: this.maxLineWidth,
        height: this.textHeight,
        noStroke: false,
        fill: true,
        noScale: false,
        noRectRefresh: true,
        hiddenMode: true,
        fillOpacity: 0.001,
        'stroke-width': 1,
        startDrag: () => {
          this.unMovedCellDims = this.cellDims;
          this.unMovedLineHeight = this.lineHeight;
        },
        drag: resp => {
          const { x, y, width: newWidth, height: newHeight, point } = resp;
          const { x: cx, y: cy } = this.unMovedCellDims;
          this.draggedDims = [newWidth, newHeight];

          if (point == 'center-top' || point == 'center-bottom') {
            if (point == 'center-top') {
              this.cell.y = (cy + y) / this.cs.currentScreenY;
              this.alignInCell(this.cell);
            }
            const newLineHeight = newHeight / (this.fontSize * 1.2);

            this.descriptor.lineHeight = newLineHeight;
            this.lines.map(line => {
              line.patchLineHeight(newLineHeight);
            });
            this.rc.updateRectangle({
              width: this.maxLineWidth,
              height: this.textHeight,
            });
            return;
          }

          this.cell.x = (cx + x) / this.cs.currentScreenX;
          this.cell.y = (cy + y) / this.cs.currentScreenY;
          this.cell.w = newWidth / this.cs.currentScreenX;
          this.cell.h = newHeight / this.cs.currentScreenY;

          this.recalcFontSize(newWidth, newHeight);
          this.alignInCell(this.cell);

          this.rc.updateRectangle({
            width: this.maxLineWidth,
            height: this.textHeight,
          });
        },
        endDrag: () => {
          if (!this.draggedDims) {
            return;
          }
          this.descriptor.cell.custom = true;
          this.save();
          this.draggedDims = null;
        },
        clicked: () => this.onCellClicked(),
        mouseover: () => this.onMouseOver(),
        mouseout: () => this.onMouseOut(),
      },
      this.circleContainer,
      this.circleContainer,
    );
  }

  endDrag() {
    this.cell.h = this.textHeight / this.cs.currentScreenY;
    super.endDrag();
  }

  async reset() {
    // this.update(this.descriptor);
    // this.lines.forEach(line => line.show());
    // this.words.forEach(word => word.show());

    await this.update(this.descriptor);
  }

  onMouseOver() {
    // console.log('onMouseOver');
    // this.rc?.rect.patch({ 'stroke-width': 1 });
    this.rc?.show();
  }
  onMouseOut() {
    // console.log('onMouseOut');
    this.rc?.hide();
    // this.rc?.rect.patch({ 'stroke-width': 0 });
  }
  onCellClicked() {
    this.rc?.show();
    this.store.dispatch(selectShape({ id: this.id }));
  }
  private subscribeToCellDescriptor() {
    if (!this.shape) {
      return;
    }
  }

  refresh() {
    this.lines.forEach(line => line.remove());
    this.words.forEach(word => word.remove());
    this.lines = [];
    this.container.alpha = 1;
    // Rebuild the text shape
    const { text } = this.descriptor;
    const rows = text.split('\n');
    if (rows && rows.length > 0) {
      this.measureAllCharacters(rows);
      this.buildLines(rows);
    }
  }

  private async init() {
    const { text } = this.descriptor;
    await this.fontService._loadFont(this.fontFamily);
    // Pass the actual text so the browser rasterizes all needed glyphs
    // (including extended Latin like ő) before canvas measurement begins.
    if (document.fonts) {
      const { fontWeight } = this.elementStyle;
      await document.fonts.load(`${fontWeight} 1em '${this.fontFamily}'`, text);
    }
    const lines = text?.split('\n');
    const [width, height] = this.grossDims;

    const { widthRatio } = this.descriptor.cell || {};
    this.calculateFontSize(lines, width * (widthRatio || 1), height);
    this.refresh();
  }

  _colorizeText() {
    // -- // -- //
    this.init().then(() => {
      this.colorizeText(this.words[0].container, this.lines[0].container, [
        // '#ffffff',
        '#7d3cff',
        '#ff3cac',
        '#ffffff',
      ]);
    });
  }

  static getStaticFontSize(
    w: number,
    h: number,
    text: string,
    fontFamily: string,
  ): number {
    const lines = text.split('\n');
    const targetWidth = w;
    const targetHeight = h;

    const style = new TextStyle({
      fontFamily,
      fontSize: 13,
    });

    const [, longestRow] = lines.reduce(
      ([longestWidth, longestText], row) => {
        const metric = TextMetrics.measureText(row, style);
        if (longestWidth < metric.maxLineWidth) {
          return [metric.maxLineWidth, row];
        } else {
          return [longestWidth, longestText];
        }
      },
      [0, ''] as [number, string],
    );

    // Binary search for optimal font size
    let minSize = 1;
    let maxSize = Math.min(targetWidth, targetHeight);
    let optimalSize = minSize;

    while (minSize <= maxSize) {
      const midSize = Math.floor((minSize + maxSize) / 2);
      const testStyle = new TextStyle({
        ...style,
        fontSize: midSize,
      });

      const metric = TextMetrics.measureText(longestRow, testStyle);
      const totalHeight = metric.lineHeight * lines.length;

      if (metric.maxLineWidth <= targetWidth && totalHeight <= targetHeight) {
        // if (metric.maxLineWidth <= targetWidth) {
        optimalSize = midSize;
        minSize = midSize + 1;
      } else {
        maxSize = midSize - 1;
      }
    }

    return Math.max(1, optimalSize);
  }

  recalcFontSize(width: number, height: number) {
    const { text } = this.descriptor;
    const rows = text.split('\n');
    this.calculateFontSize(rows, width, height);
    this.measureAllCharacters(rows);
    this.buildLines(rows);
  }

  getDimensions() {
    return [this.maxLineWidth, this.baseLineHeight * this.lines.length];
  }

  get multipleLettersSelected() {
    return (
      this.selectionStartIndex != null &&
      this.selectionStartIndex !== this.selectionEndIndex
    );
  }

  updateSelection() {
    // this.lines.forEach(line => line.updateSelection());
  }

  get fontService() {
    return this.shape.studioService.shapeService.fontService;
  }

  static getFontSizeByCell(
    cs: CanvasService,
    cell: StudioCell,
    text: string,
    fontFamily: string,
  ): number {
    const lines = text.split('\n');
    const [screenX, screenY] = [cs.currentScreenX, cs.currentScreenY].map(
      val => val * cs.fitScale,
    );
    const { w, h } = cell;
    const targetWidth = screenX * w;
    const targetHeight = screenY * h;

    const style = new TextStyle({
      fontFamily,
      fontSize: 13,
    });

    const [, longestRow] = lines.reduce(
      ([longestWidth, longestText], row) => {
        // const rowText = row.join(' ');
        const metric = TextMetrics.measureText(row, style);
        if (longestWidth < metric.maxLineWidth) {
          return [metric.maxLineWidth, row];
        } else {
          return [longestWidth, longestText];
        }
      },
      [0, ''] as [number, string],
    );

    // Binary search for optimal font size
    let minSize = 1;
    let maxSize = Math.min(targetWidth, targetHeight);
    let optimalSize = minSize;

    while (minSize <= maxSize) {
      const midSize = Math.floor((minSize + maxSize) / 2);
      const testStyle = new TextStyle({
        ...style,
        fontSize: midSize,
      });

      const metric = TextMetrics.measureText(longestRow, testStyle);
      const totalHeight = metric.lineHeight * lines.length;

      if (metric.maxLineWidth <= targetWidth && totalHeight <= targetHeight) {
        optimalSize = midSize;
        minSize = midSize + 1;
      } else {
        maxSize = midSize - 1;
      }
    }

    return Math.max(1, optimalSize);
  }

  private calculateFontSize(
    rows: Array<string>,
    targetWidth: number,
    targetHeight: number,
  ): Promise<void> {
    // return new Promise(resolve => {
    // Find the longest row text
    if (this.descriptor.cell.fontSize) {
      this.fontSize = this.descriptor.cell.fontSize;
    } else {
      const _style = new TextStyle({
        ...this.elementStyle,
        fontSize: 20,
      });

      const [, longestRow] = rows.reduce(
        ([longestWidth, longestText], row) => {
          // const rowText = row.join(' ');
          const metric = TextMetrics.measureText(row, _style);
          if (longestWidth < metric.maxLineWidth) {
            return [metric.maxLineWidth, row];
          } else {
            return [longestWidth, longestText];
          }
        },
        [0, ''] as [number, string],
      );

      if (!longestRow) {
        this.fontSize = 28;
        return;
      }

      // Binary search for optimal font size
      let minSize = 1;
      let maxSize = Math.min(targetWidth, targetHeight);
      let optimalSize = minSize;

      while (minSize <= maxSize) {
        const midSize = Math.floor((minSize + maxSize) / 2);
        const testStyle = new TextStyle({
          ...this.elementStyle,
          fontSize: midSize,
        });

        const metric = TextMetrics.measureText(longestRow, testStyle);
        const totalHeight = metric.lineHeight * rows.length;

        if (metric.maxLineWidth <= targetWidth && totalHeight <= targetHeight) {
          optimalSize = midSize;
          minSize = midSize + 1;
        } else {
          maxSize = midSize - 1;
        }
      }

      // optimalSize = 124;
      this.fontSize = Math.max(1, optimalSize);
    }

    const style = new TextStyle({
      ...this.elementStyle,
      fontSize: this.fontSize,
    });
    const metric = TextMetrics.measureText('Abc', style);
    this.baseLineHeight = metric.lineHeight;
    this.netLineHeight = metric.lineHeight;

    let maxWidth = 0;
    for (const row of rows) {
      const rowMetric = TextMetrics.measureText(row, style);
      maxWidth = Math.max(maxWidth, rowMetric.maxLineWidth);
    }
    this.maxLineWidth = maxWidth;

    // Calculate space width
    const outerMetric = TextMetrics.measureText('a', style);
    const spaceMetric = TextMetrics.measureText('a a', style);
    this.spaceWidth = spaceMetric.maxLineWidth - 2 * outerMetric.maxLineWidth;
  }

  private measureAllCharacters(rows: Array<string>) {
    this.characterWidthMap = {};

    const { fontFamily, fontWeight } = this.elementStyle;
    const fontSpec = `${fontWeight} 1em '${fontFamily}'`;
    if (document.fonts && !document.fonts.check(fontSpec)) {
      console.warn(`[measureAllCharacters] Font not yet loaded: ${fontSpec}`);
    }

    const style = new TextStyle({
      ...this.elementStyle,
      fontSize: this.fontSize,
    });

    for (const row of rows) {
      for (const word of row) {
        for (const char of word) {
          if (this.characterWidthMap[char]) continue;
          if (char === ' ') {
            this.characterWidthMap[char] = this.spaceWidth;
            continue;
          }
          const metric = TextMetrics.measureText(char, style);
          if (char == 'ő') {
            console.log('ő', metric.maxLineWidth);
          }
          this.characterWidthMap[char] = metric.maxLineWidth;
        }
      }
    }
    // Always add space
    this.characterWidthMap[' '] = this.spaceWidth;
  }

  private async buildLines(rows: Array<string>) {
    this.lines.forEach(line => line.remove());
    this.lines = [];
    this.letterIndex = 0;

    const style = new TextStyle({
      ...this.elementStyle,
      fontSize: this.fontSize,
    });
    const metric = TextMetrics.measureText('Abc', style);
    this.baseLineHeight = metric.lineHeight;

    this.lines = rows.map((row, index) => {
      const wordsData = this.buildWordData(row);
      const lineWidth = wordsData.reduce((sum, w) => sum + w.width, 0);
      const line = new TextLine(
        this,
        {
          words: wordsData,
          wrapped: false,
          width: lineWidth,
        },
        {
          index,
          maxLineWidth: this.maxLineWidth,
          lineHeight: this.lineHeight * this.fontSize * 1.2,
          letterIndexOffset: 0,
          style: this.elementStyle as ITextStyle,
          width: lineWidth,
        },
      );

      return line;
    });
  }

  private buildWordData(words: string): Array<{ text: string; width: number }> {
    const result: Array<{ text: string; width: number }> = [];

    const wordsArray = words.split(' ');
    wordsArray.forEach((word, i) => {
      const width = Array.from(word).reduce(
        (sum, char) => sum + (this.characterWidthMap[char] || 0),
        0,
      );
      result.push({ text: word, width });
      //   result.push({ text: ' ', width: this.spaceWidth }); //

      if (i < wordsArray.length - 1) {
        result.push({ text: ' ', width: this.spaceWidth });
      }
    });

    return result;
  }

  getGsapPosition(delay: number, overlap: number) {
    return delay !== undefined
      ? delay
      : overlap !== undefined
        ? `-=${overlap}`
        : 0;
  }

  getLetterIndex(): number {
    return this.letterIndex++;
  }

  // Stub methods that may be called by TextLine/TextWord/TextLetter
  removeSelection() {}
  showCursor() {}
  clicked() {}
  select(_params?: unknown) {}
  deselect(_params?: unknown) {}
  keyDownHandler(_key: string) {}
  _showCursor(_position: { x: number; y: number }, _word: TextWord) {}
  destroy() {
    // Unsubscribe from cell descriptor changes
    console.log('studio-text-shape > remove');
    if (this.cellSubscription) {
      this.cellSubscription.unsubscribe();
      this.cellSubscription = null;
    }

    this.lines.forEach(line => line.remove());
    this.container.destroy();
  }

  remove() {
    console.log('studio-text-shape > remove');
    this.destroy();
  }

  baseHeight: number;
  /**
   * Animate the text shape with a given animation step
   */

  // ── Gradient colorization ──────────────────────────────────────────────────
  // Direct port of getGradientTexture / colorizeText from grad_text_simplified.html.
  // container     = word's pixi Container (lettersContainer in POC)
  // wordContainer = line's pixi Container (wordContainer in POC)

  private getGradientTexture(
    w: number,
    h: number,
    colors: string[],
    currentColor: string,
  ): Texture {
    // Canvas is 2× word width: left half = gradient, right half = solid currentColor.
    // The TilingSprite starts showing the right (solid) half and slides right to reveal the gradient.
    const halfW = Math.max(2, Math.floor(w));
    const fullW = halfW * 2;
    const ch = Math.max(2, Math.floor(h));
    const canvas = document.createElement('canvas');
    canvas.width = fullW;
    canvas.height = ch;
    const ctx = canvas.getContext('2d')!;

    // Left half: gradient  c1 → c2 → c3 → currentColor
    const allColors = [...colors, currentColor];
    const grad = ctx.createLinearGradient(0, 0, halfW, 0);
    const step = 1 / Math.max(1, allColors.length - 1);
    allColors.forEach((color, i) => grad.addColorStop(i * step, color));
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, halfW, ch);

    // Right half: solid currentColor
    ctx.fillStyle = currentColor;
    ctx.fillRect(halfW, 0, halfW, ch);

    return Texture.from(canvas);
  }

  colorizeText(
    lettersContainer: Container,
    wordContainer: Container,
    colors: string[],
  ): TilingSprite {
    Array.from(lettersContainer.children).forEach(c => {
      (c as Text).style.fill = 0xffffff;
    });

    const b = lettersContainer.getLocalBounds();
    const currentColor = this.descriptor.fill || '#000000';
    const gradTexture = this.getGradientTexture(
      b.width,
      b.height,
      colors,
      currentColor,
    );
    // Gradient texture is 2× width. Show the right (solid) half initially
    // by offsetting tilePosition. Animate tilePosition.x → 0 to reveal gradient.
    const gradient = new TilingSprite(gradTexture, b.width, b.height);
    gradient.tilePosition.x = -b.width;
    // Account for word's position within the parent (line) container
    gradient.x = lettersContainer.x + b.x;
    gradient.y = lettersContainer.y + b.y;

    wordContainer.addChildAt(gradient, 0);
    const rt = this.cs.app.renderer.generateTexture(lettersContainer, {
      region: b,
      resolution: this.cs.app.renderer.resolution,
    });
    const maskSprite = new Sprite(rt);
    maskSprite.x = lettersContainer.x + b.x;
    maskSprite.y = lettersContainer.y + b.y;

    wordContainer.addChildAt(maskSprite, 1);
    gradient.mask = maskSprite;
    lettersContainer.visible = false;
    return gradient;
  }

  // ── Animation steps ────────────────────────────────────────────────────────

  onStartFcns: Array<() => void> = [];

  gradientMaskSprite: Sprite;
  applyGradientMask(animation: StudioTextAnimationStep) {
    // todo - implement
    this.gradientMaskSprite = this.gradientMaskAnimation(
      animation as StudioAnimation,
    );
    // -- // -- // -- //
    this.lines[0].container.mask = this.gradientMaskSprite;

    console.log('applyGradientMask', this.gradientMaskSprite);
    const timeline = gsap.timeline();

    timeline.add(
      gsap.to(this.gradientMaskSprite, {
        duration: animation.duration || 1,
        x: 0,
        y: 0,
        ease: 'power1.inOut',
      }),
    );
    return timeline;
  }

  getAnimationFunction(key: string) {
    const animation = super.getAnimationFunction(key);
    if (animation) {
      return animation;
    }
  }
  gsapAnimateStep(
    _animation: StudioTextAnimationStep,
    _timeline?: gsap.core.Timeline,
    force = false,
  ) {
    this.onStartFcns = [];

    const animation: StudioTextAnimationStep = force
      ? _animation
      : this.getAnimationFunction(_animation.type) || _animation;

    // -- //
    // console.log('gsapAnimateStep', animation);
    const timeline = gsap.timeline();
    const {
      type: baseType,
      duration: initialDuration,
      zoom,
      translate,
      line,
      word,
      blur,
      mask,
      letter,
      hold,
      gradientMask,
      colorize,
    } = animation;

    const duration = initialDuration || 1;

    this.baseHeight = this.fontSize * this.lines.length;

    if (gradientMask) {
      const maskTimeline = this.applyGradientMask(animation);
      timeline.add(maskTimeline, 0);
    }

    if (colorize) {
      const { wordIndex, gradient } = colorize;
      const word = this.words[wordIndex];
      word.reInit();
      const gradientSprite = this.colorizeText(
        word.container,
        word.parent.container,
        gradient,
      );
      // Slide tilePosition from -width (solid currentColor) to 0 (gradient)
      timeline.add(
        gsap.to(gradientSprite.tilePosition, {
          x: 0,
          duration,
          ease: 'power1.inOut',
        }),
      );
    }

    if (baseType === 'appear') {
      if (!line && !word && !colorize) {
        this.container.alpha = 0;
        timeline.add(
          gsap.to(this.container, { alpha: 1, duration: duration - 0.3 }),
        );
      }
    }

    if (baseType === 'disappear') {
      if (!line && !word) {
        // The line is defined the appear logic takes place in the line animation
        timeline.add(gsap.to(this.container, { alpha: 0, duration }));
      }
    }

    if (zoom) {
      console.log('zoom', zoom);
      const zoomTimeline = this.gsapZoomAnimateStep(zoom, duration);
      timeline.add(zoomTimeline, 0);
    }

    if (translate) {
      const translateTimeline = this.gsapTranslateAnimateStep(
        animation,
        duration,
      );
      timeline.add(translateTimeline, 0);
    }

    // Handle line animation - check if lineIndex is defined for single line animation
    if (line || word) {
      const completeDuration = duration - (hold || 0);
      const itemDuration = this.studioService.getItemDuration(
        this.lines.length,
        completeDuration,
        line?.overlap || 0,
      );
      const lineTimelines = this.lines.map(line =>
        line.gsapAnimate({
          ...animation,
          duration: itemDuration,
        }),
      );
      // -- //
      this.onStartFcns.push(...lineTimelines.map(l => l?.onStartFcns).flat());
      this.studioService.addAnimationChain(
        timeline,
        lineTimelines.map(l => l.timeline),
        {
          completeDuration,
          overlap: line?.overlap,
        },
      );
    }

    // if (word && !line) {
    //   const { overlap: wordOverlap } = word;
    //   const completeDuration = duration - (hold || 0);
    //   const itemDuration = this.studioService.getItemDuration(
    //     this.words.length,
    //     completeDuration,
    //     wordOverlap,
    //   );
    //   const wordTimelines = this.words.map(word =>
    //     word.gsapAnimate({
    //       ...animation,
    //       duration: itemDuration,
    //     }),
    //   );

    //   this.studioService.addAnimationChain(timeline, wordTimelines, {
    //     completeDuration,
    //     overlap: wordOverlap || 0,
    //   });
    // }

    // if (letter) {
    //   const { overlap: letterOverlap } = letter;
    //   const completeDuration = duration - (hold || 0);
    //   const itemDuration = this.studioService.getItemDuration(
    //     this.letters.length,
    //     completeDuration,
    //     letterOverlap,
    //   );
    //   const letterTimelines = this.letters.map(letter =>
    //     letter.gsapAnimate({
    //       ...animation,
    //       duration: itemDuration,
    //     }),
    //   );
    //   this.studioService.addAnimationChain(timeline, letterTimelines, {
    //     completeDuration,
    //     overlap: letterOverlap || 0,
    //   });
    // }
    // Handle letter-by-letter animation

    // Handle nextLine - chain the next line's animation to the timeline
    // if (nextLine) {
    //   const nextLineTimeline = this.gsapAnimateStep(nextLine);
    //   const overlapTime = nextLine.overlap ? duration * nextLine.overlap : 0;
    //   if (overlapTime > 0) {
    //     timeline.add(nextLineTimeline, `-=${overlapTime}`);
    //   } else {
    //     timeline.add(nextLineTimeline, `+=${nextLine.delay || 0}`);
    //   }
    // }

    if (blur) {
      const blurTimeline = gsap.timeline();
      const { from, duration: blurDuration } = blur;
      const blurFilter = new BlurFilter(from, 5);
      blurFilter.padding = Math.ceil(blur.to) * 2;
      this.container.filters = [blurFilter];
      timeline.add(blurTimeline, 0);
    }

    timeline.eventCallback('onStart', () => {
      this.onStartFcns.map(fcn => fcn());
    });

    return timeline;
  }

  /**
   * Animate a single line by its index
   */

  gsapTranslateAnimateStep(
    animation: StudioTextAnimationStep,
    duration: number,
  ) {
    const timeline = gsap.timeline();
    const { translate, type: baseType } = animation;
    const { from, to, duration: translateDuration, mask } = translate;

    if (mask) {
      const maskConfig = {
        x: this.container.x,
        y: this.container.y,
        position: { x: this.container.x, y: this.container.y },
        width: this.maxLineWidth,
        height: 1.3 * this.fontSize * this.lines.length,
      };

      if (!this.maskRectangle) {
        // Create
        this.maskRectangle = new RectangleElement(this, this.container, {
          ...maskConfig,
          fill: '#000000',
          opacity: 0.001,
        });
        this.shape.container.addChild(this.maskRectangle.element);
      } else {
        // Edit
        this.maskRectangle.patch(maskConfig);
      }

      this.container.mask = this.maskRectangle.element;
      timeline.eventCallback('onComplete', () => this._destroyMaskRectangle());
    } else {
      // Delete
      this._destroyMaskRectangle();
    }
    const dur = translateDuration || duration;

    if (baseType === 'appear' && from) {
      const originalX = this.container.x;
      const originalY = this.container.y;
      // Coords is [x, y] tuple
      this.container.x = originalX + (from[0] || 0);
      this.container.y = originalY + (from[1] || 0);
      this.container.alpha = 0;

      // console.log('set-container-alpha > 0', from);
      timeline.add(
        gsap.to(this.container, {
          duration: dur,
          x: originalX,
          y: originalY,
          alpha: 1,
          ease: 'power2.out',
        }),
        0,
      );
    } else if (baseType === 'disappear' && to) {
      timeline.add(
        gsap.to(this.container, {
          duration: dur,
          x: this.container.x + (to[0] || 0),
          y: this.container.y + (to[1] || 0),
          alpha: 0,
          ease: 'power2.in',
        }),
        0,
      );
    }

    if (baseType === 'move' && to) {
      const { x, y } = to as any;
      const [width, height] = [this.shape.screenWidth, this.shape.screenHeight];
      console.log('move', 'x', x, 'y', y);
      console.log('xOffset', this.shape.xOffset, 'yOffset', this.shape.yOffset);
      timeline.add(
        gsap.to(this.container.position, {
          duration: dur,
          x: this.shape.xOffset + this.alignOffsetX + x * width,
          y: this.shape.yOffset + this.alignOffsetY + y * height,
          ease: 'power2.out',
        }),
        0,
      );
    }

    return timeline;
  }

  private _destroyMaskRectangle() {
    if (this.maskRectangle) {
      this.container.mask = null;
      this.maskRectangle.element.parent?.removeChild(
        this.maskRectangle.element,
      );
      this.maskRectangle.element.destroy();
      this.maskRectangle = null;
    }
  }

  gsapZoomAnimateStep(animation: ZoomAnimation, duration: number) {
    // -- // -- // -- //

    const timeline = gsap.timeline();
    const { from, to, duration: zoomDuration, translate } = animation;
    const [zoomDx, zoomDy] = translate || [0, 0];
    if (from) {
      // console.log('bounds', this.container.getBounds()); //
      // console.log('bounds', this.container.width, this.container.height); //
      timeline.eventCallback('onStart', () => {
        const fromScale = from;
        const previousWidth = this.maxLineWidth;
        const nextWidth = this.maxLineWidth * fromScale;

        const __dx = (previousWidth - nextWidth) / 2;
        const __dy = (this.baseHeight - this.baseHeight * fromScale) / 2;

        this.container.scale.x = fromScale;
        this.container.scale.y = fromScale;

        /* console.log('------ from ------', {
          fromScale,
          prevX: this.container.position.x,
          prevY: this.container.position.y,
          __dx,
          __dy,
        }); */
        this.container.position.x += __dx;
        this.container.position.y += __dy;
      });

      this.container.scale.set(from);

      // timeline.add(
      //   gsap.to(this.container.scale, {
      //     duration,
      //     x: to,
      //     y: to,
      //     alpha: 1,
      //     // ease: 'power2.out',
      //   }),
      //   0,
      // );
    }

    if (to) {
      timeline.add(
        gsap.to(this.container.position, {
          duration: zoomDuration || duration || 1,
          x: (i, target) => {
            // Original
            const targetWidth = this.maxLineWidth * to;
            const currentWidth = this.maxLineWidth * this.container.scale.x;
            const dx = (targetWidth - currentWidth) / 2;
            return target.x - dx + zoomDx;
          },
          y: (i, target) => {
            const targetHeight = this.baseHeight * to;
            const currentHeight = this.baseHeight * this.container.scale.y;
            return target.y - (targetHeight - currentHeight) / 2 + zoomDy;
          },
          // ease: 'linear',
          // ease: 'power1.out',
        }),
        0,
      );
      timeline.add(
        gsap.to(this.container.scale, {
          duration,
          x: to,
          y: to,
          alpha: 0,
          // ease: 'power2.in',
        }),
        0,
      );
    }

    return timeline;
  }

  gsapLineAnimateStep(animation: StudioTextAnimationStep) {
    const timeline = gsap.timeline();

    const { line } = animation;
    const { delay } = line || {};

    const lineAnimations = this.lines.map(line => line.gsapAnimate(animation));

    // -- // -- //∂
    const lineStartFcns: Array<() => void> = [];
    lineAnimations.forEach((animation, index) => {
      const { timeline: lineTimeline, onStartFcns: lineOnStartFcns } =
        animation;
      timeline.add(lineTimeline, index * (delay || 0));
      lineStartFcns.push(...lineOnStartFcns);
    });
    // -- //
    return { timeline, onStartFcns: lineStartFcns };
  }

  /**
   * Generate animation step based on animation type
   */
  static generateAnimationStep(
    type: StudioTextAnimationType,
    options?: { duration?: number; translateFrom?: [number, number] },
  ): TextAnimationStep {
    const duration = options?.duration || 1.5;

    switch (type) {
      case 'zoom-fade':
        return {
          type: 'appear',
          duration,
          forceFade: true,
          zoom: {
            from: 0.8,
            to: 1,
          },
        };

      case 'fade-by-letters':
        return {
          type: 'appear',
          duration,
          letter: {},
        };

      case 'translate-fade':
        return {
          type: 'appear',
          duration,
          forceFade: true,
          translate: {
            type: 'absolute',
            from: options?.translateFrom || [0, 50],
          },
        };

      default:
        return {
          type: 'appear',
          duration,
          forceFade: true,
        };
    }
  }

  createContainerDropShadow() {
    // this.container.filters = [
    //   new DropShadowFilter({
    //     alpha: 0.35,
    //     blur: 24,
    //     color: 0x000000,
    //     distance: 0,
    //     quality: 2,
    //     rotation: 0,
    //   }),
    // ];
  }
  currentLetter: TextLetter;
  letterClicked(letter: TextLetter) {
    this.currentLetter = letter;
  }
}
